import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Shield, FileText, Calculator, Users, Briefcase, Wallet,
  Scale, Server, Megaphone, HeartHandshake, Cog, ArrowUpRight,
  ScrollText, UserPlus, Settings
} from "lucide-react";

// 13 setores na ordem exata do painel original SOEASY
const SETORES = [
  { id: "cct", abbrev: "CCT", label: "Convenções Coletivas de Trabalho", desc: "Gestão de CCTs por UF. Carregue PDFs via IA ou cadastre manualmente. Base do cálculo de orçamentos.", icon: ScrollText, path: "/orcamentos", accent: "#F472B6", active: true },
  { id: "orcamento_sgo", abbrev: "SGO", label: "Orçamento", desc: "Sistema de Gestão de Orçamento com CCT, cálculo de custos, formação de preço e proposta final.", icon: Calculator, path: "/orcamentos", accent: "#34D399", active: true },
  { id: "vendas", abbrev: "VENDAS", label: "Comercial", desc: "CRM Comercial — Clientes, Funil de Vendas, Orçamentos, Contratos e OS de Abertura.", icon: Briefcase, path: "#", accent: "#60A5FA" },
  { id: "atendimento", abbrev: null, label: "Atendimento ao Cliente", desc: "Customer Success. Relacionamento contínuo, retenção e geração de novos negócios.", icon: HeartHandshake, path: "/atendimento", accent: "#34D399", active: true },

  { id: "operacoes", abbrev: "OPERAÇÕES", label: "Produção", desc: "Execução e entrega de serviços. Controle de escala, postos e equipes operacionais.", icon: Cog, path: "#", accent: "#FBBF24" },
  { id: "rh", abbrev: "DP", label: "Recursos Humanos", desc: "Contratação, treinamento, aprovação de colaboradores e gestão de acessos ao sistema.", icon: Users, path: "/rh", accent: "#A78BFA", active: true },
  { id: "colaboradores", abbrev: null, label: "Colaboradores Extra", desc: "Gestão de colaboradores extras e temporários.", icon: UserPlus, path: "#", accent: "#818CF8" },
  { id: "financeiro", abbrev: "CONTÁBIL", label: "Financeiro", desc: "Fluxo de caixa, pagamentos, recebimentos e conformidade tributária e fiscal.", icon: Wallet, path: "/financeiro", accent: "#22D3EE", active: true },

  { id: "administrativo", abbrev: "JURÍDICO", label: "Administrativo", desc: "Contratos, proteção legal, gestão estratégica de processos e adequação normativa.", icon: Scale, path: "/administrativo", accent: "#F87171", active: true },
  { id: "ti", abbrev: null, label: "Tecnologia da Informação", desc: "Central de chamados, base de conhecimento, status dos sistemas e suporte com IA.", icon: Server, path: "/ti", accent: "#22D3EE", active: true, hasAdminPanel: true },
  { id: "documentos", abbrev: "DOCUMENTOS", label: "Integração", desc: "Governança de documentos, assinatura digital, conformidade e pontos de desempenho.", icon: FileText, path: "/documentos", accent: "#818CF8", active: true },
  { id: "compliance", abbrev: "COMPLIANCE", label: "Governança", desc: "Processos disciplinares, conformidade corporativa e governança interna.", icon: Shield, path: "/compliance", accent: "#B28F4B", active: true, featured: true },

  { id: "marketing", abbrev: null, label: "Marketing", desc: "Imagem institucional, comunicação de valor e atração de novos clientes.", icon: Megaphone, path: "/marketing", accent: "#F472B6", active: true },
];

export default function Setores() {
  const { user, logout } = useAuth();
  const isMaster = user?.nivel === "admin";

  return (
    <div className="min-h-screen bg-[#0A0B1A]">
      {/* Header fixo estilo institucional */}
      <header className="sticky top-0 z-20 bg-gradient-to-r from-[#1A1D42] to-[#252952] border-b border-[#B28F4B]/20 shadow-[0_4px_20px_rgba(26,29,66,0.4)]">
        <div className="max-w-[1440px] mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-full bg-[#0A0B1A] border-2 border-[#B28F4B] flex items-center justify-center gold-glow">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L4 6v6c0 5 3.5 9.5 8 10 4.5-0.5 8-5 8-10V6l-8-4z" stroke="#B28F4B" strokeWidth="1.5" fill="rgba(178,143,75,0.15)"/>
                <path d="M9 12l2 2 4-4" stroke="#B28F4B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="flex items-baseline gap-2">
              <div className="text-[#B28F4B] text-xl md:text-2xl font-bold font-heading tracking-tight">GRUPO SOEASY</div>
              <div className="hidden md:block text-white/50 text-sm font-medium uppercase tracking-widest">Painel de Controle</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden md:flex flex-col items-end gap-1">
              {isMaster && (
                <div className="bg-[#B28F4B]/20 border border-[#B28F4B]/50 text-[#D4AF37] text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider whitespace-nowrap">
                  Você está logado com a senha MASTER
                </div>
              )}
              <div className="bg-[#B28F4B]/20 border border-[#B28F4B]/50 text-[#D4AF37] text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider whitespace-nowrap">
                Seja bem-vindo Sr. {user?.nome}
              </div>
            </div>
            <button
              onClick={() => { logout(); window.location.href = "/login"; }}
              data-testid="btn-header-logout"
              className="px-5 py-2 rounded-lg bg-red-500/20 border border-red-500/40 text-red-300 hover:bg-red-500/30 hover:text-red-200 font-bold text-xs uppercase tracking-widest transition"
            >
              Sair
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1440px] mx-auto p-6 md:p-10 fade-up">
        {/* Título */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl md:text-4xl font-light text-white leading-tight">
            Selecione o <span className="text-[#B28F4B] font-medium">Setor</span>
          </h1>
          <p className="text-[#A1A4C7] text-sm mt-2">Acesse o módulo do seu setor ou de um setor ao qual você foi autorizado.</p>
        </div>

        {/* Grid 4 colunas — mesmo layout do painel original */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {SETORES.map((s, idx) => {
            const Wrapper = s.active ? Link : "div";
            const canClick = s.active;
            return (
              <Wrapper
                key={s.id}
                to={s.path}
                data-testid={`setor-${s.id}`}
                className={`soeasy-card p-6 relative group flex flex-col ${canClick ? "cursor-pointer hover:-translate-y-1" : "opacity-60"} ${s.featured ? "ring-2 ring-[#B28F4B]/40" : ""}`}
                style={{
                  animationDelay: `${idx * 30}ms`,
                  background: s.featured ? "linear-gradient(135deg, #1F2350 0%, #131532 100%)" : undefined
                }}
              >
                {!s.active && (
                  <div className="absolute top-3 right-3 text-[9px] font-bold text-[#B28F4B]/70 uppercase tracking-wider bg-[#B28F4B]/10 border border-[#B28F4B]/25 px-2 py-0.5 rounded-full">
                    Em breve
                  </div>
                )}
                {s.featured && (
                  <div className="absolute top-3 right-3 text-[9px] font-bold text-[#B28F4B] uppercase tracking-wider bg-[#B28F4B]/15 border border-[#B28F4B]/40 px-2 py-0.5 rounded-full">
                    ★ Principal
                  </div>
                )}

                {/* Ícone */}
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
                  style={{ background: `${s.accent}15`, border: `1px solid ${s.accent}35` }}
                >
                  <s.icon size={26} strokeWidth={1.4} style={{ color: s.accent }} />
                </div>

                {/* Título com abreviatura no topo */}
                <div className="flex-1">
                  {s.abbrev && (
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B6E8F] mb-1">{s.abbrev}</div>
                  )}
                  <h3 className="font-heading text-lg font-semibold text-white leading-tight mb-3 group-hover:text-[#B28F4B] transition-colors uppercase tracking-wide">
                    {s.label}
                  </h3>
                  <p className="text-[#A1A4C7] text-xs leading-relaxed">{s.desc}</p>
                </div>

                {/* Painel Admin — só para Master no TI */}
                {s.hasAdminPanel && isMaster && (
                  <Link
                    to="/admin/usuarios"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="btn-admin-panel"
                    className="mt-4 inline-flex items-center gap-2 bg-[#B28F4B] hover:bg-[#D4AF37] text-[#1A1D42] font-bold text-[10px] uppercase tracking-widest px-3 py-2 rounded-lg transition"
                  >
                    <Settings size={11} strokeWidth={2} />
                    Painel Admin — Usuários e Acessos
                  </Link>
                )}

                {canClick && (
                  <div className="mt-4 flex items-center gap-1.5 text-[#B28F4B] text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity uppercase tracking-widest">
                    Acessar <ArrowUpRight size={12} />
                  </div>
                )}
              </Wrapper>
            );
          })}
        </div>

        <div className="mt-10 text-center text-[#6B6E8F] text-[10px] font-mono uppercase tracking-widest">
          © 2026 SOEASY Security · Plataforma Enterprise v1.0
        </div>
      </div>
    </div>
  );
}
